import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeclinePublicReason, TriageQueueItem } from '@munifocus/contracts-gov';
import { api } from './api-client';

export const triageKeys = {
  all: ['triage'] as const,
  list: (status?: string) => ['triage', status ?? 'open'] as const,
};

/**
 * Public reports waiting on a decision.
 *
 * Scoped server-side to the municipalities the caller can work in, so nothing
 * here filters — see `TriageController.triageScope` for why that union is of two
 * permissions rather than one.
 */
export function useTriageQueue(status?: string) {
  return useQuery({
    queryKey: triageKeys.list(status),
    queryFn: async () => {
      const { data } = await api.get<TriageQueueItem[]>('/triage', {
        params: status ? { status } : undefined,
      });
      return data;
    },
  });
}

export function useDeclineReport() {
  const queryClient = useQueryClient();
  return useMutation({
    // The screen reports the failure itself.
    meta: { silent: true },
    mutationFn: async ({
      id,
      reason,
      publicReason,
    }: {
      id: string;
      reason: string;
      /** What a citizen reporter is told. Only for citizen reports. */
      publicReason?: DeclinePublicReason;
    }) => {
      const { data } = await api.post<TriageQueueItem>(`/triage/${id}/decline`, {
        reason,
        ...(publicReason ? { publicReason } : {}),
      });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: triageKeys.all }),
  });
}

/** Fold a report into a file already open — "this is a duplicate". */
export function useMergeReport() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silent: true },
    mutationFn: async ({ id, fileId, note }: { id: string; fileId: string; note?: string }) => {
      const { data } = await api.post<TriageQueueItem>(`/triage/${id}/merge`, {
        fileId,
        ...(note ? { note } : {}),
      });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: triageKeys.all }),
  });
}
